import React, {useEffect, useState} from "react";
import {useStore} from "../../hooks/useStore";
import {useObserver} from "mobx-react-lite";
import {useHistory} from "react-router-dom";
import {activityColors, userTypes} from "../../constants/constants";
import {Grid, Table, Row, Col, Badge, message, Button} from "antd";
import {
    UserAddOutlined,
    FileSearchOutlined,
    SearchOutlined,
    CheckOutlined,
    CloseOutlined
} from "@ant-design/icons";
import {useTranslation} from "react-i18next";

import {useFetch} from "../../hooks/useFetch";
import {isConstantExist, getConstant} from "../../utils/helpers";
import {CustomButton} from "../_defaultComponents/CustomButton/CustomButton";

import {RightButton, TableContainer} from "./userPageStyles";
import {
    CloseIcon,
    SearchInput,
    SelectField
} from "../_defaultComponents/FilterComponents/FilterComponentStyles";
import {UserTypeFilter} from "../_defaultComponents/FilterComponents";
import {USER_TICKET_VIEWER_ROLES} from "../../constants/ticket";
import {agencyListedRoles} from "../../constants/constants";
import {EmptyMessage} from "../_defaultComponents/EmptyMessage/EmptyMessage";
import {ResponsiveDataTable} from "components/_defaultComponents/ResponsiveDataTable";
import {ButtonGroup, DetailButton, SelectButtonContainer} from "./styles/UserPage.styled";
const {Column} = Table;

export const UserPage = () => {
    const history = useHistory();
    const [currentPage, setCurrentPage] = useState(1);
    const [activity, setActivity] = useState(
        new URLSearchParams(history.location.search).get("activity") || "enabled"
    );
    const stores = useStore();
    // Acente yalnızca cUser/subAgency/agencyIk görür; toplu aktif/pasif işlemi (bulk-update) ona kapalıdır.
    const isAgency = stores.meStore.checkRole("isAgency");
    // Salt okunur roller kullanıcıları yalnızca görüntüler; toplu aktif/pasif işlemi kapalıdır.
    const isReadOnlyViewer = USER_TICKET_VIEWER_ROLES.includes(stores.meStore.role);
    const canBulkUpdate = !isAgency && !isReadOnlyViewer;
    const {t} = useTranslation(["userPage", "userTypes"]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [searchText, setSearchText] = useState(
        new URLSearchParams(history.location.search).get("searchText")
    );
    const [role, setRole] = useState(
        new URLSearchParams(history.location.search).get("role") || "all"
    );
    const [text, setText] = useState("");
    const [loading, data, setData, setLoading] = useFetch({
        fetchFn: stores.userStore.getUserList.bind(null, {
            limit: 10,
            skip: (currentPage - 1) * 10,
            activity,
            searchText: searchText && searchText.toLocaleLowerCase("tr-TR"),
            role
        }),
        dependencies: [currentPage, activity, searchText, role]
    });

    const handleRowSelection = (selectedRowKeys, selectedRows) => {
        if (selectAll && selectedRowKeys.length === data.rows.length) {
            const existingKeys = selectedRowKeys.filter(
                key => !data.rows.some(row => row._id === key)
            );
            setSelectedRowKeys([...existingKeys, ...selectedRowKeys]);

            const existingRows = selectedRows.filter(
                row => !data.rows.some(dataRow => dataRow._id === row._id)
            );
            setSelectedRows([...existingRows, ...selectedRows]);
        } else {
            setSelectedRowKeys(selectedRowKeys);
            setSelectedRows(selectedRows);
            setSelectAll(false);
        }
    };

    const selectAllData = async () => {
        setSelectAll(true);
        setLoading(true);

        try {
            const allData = await stores.userStore.getUserList({
                limit: 1000000,
                skip: 0,
                activity,
                searchText: searchText && searchText.toLocaleLowerCase("tr-TR"),
                role
            });

            if (allData && allData.rows && allData.rows.length > 0) {
                const allKeys = allData.rows.map(row => row._id);
                setSelectedRowKeys(allKeys);
                setSelectedRows(allData.rows);
            } else {
                message.info("Seçilecek kullanıcı bulunamadı");
            }
        } catch (error) {
            console.error("Tüm verileri getirirken hata oluştu:", error);
            message.error("Tüm kullanıcıları seçerken bir hata oluştu");
        } finally {
            setLoading(false);
        }
    };

    const clearAllSelections = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setSelectAll(false);
    };

    useEffect(() => {
        const urlSearchParams = new URLSearchParams(history.location.search);
        const urlPage = urlSearchParams.get("page");
        const urlActivity = urlSearchParams.get("activity");
        const urlSearchText = urlSearchParams.get("searchText");
        const urlRole = urlSearchParams.get("role");
        setTimeout(() => {
            if (urlPage) {
                const page = parseInt(urlPage);
                if (!isNaN(page)) setCurrentPage(page);
            }
            if (urlActivity) {
                setActivity(urlActivity);
            }
            if (urlSearchText) {
                setSearchText(urlSearchText);
                setText(urlSearchText);
            }
            if (urlRole) {
                setRole(urlRole);
            }
        });
    }, []);

    useEffect(() => {
        setUrl();
    }, [currentPage, activity, searchText, role]);

    const setUrl = () => {
        let path = history.location.pathname;
        if (currentPage) {
            path += "?page=" + currentPage;
        }
        if (activity) {
            path += "&activity=" + activity;
        }
        if (role) {
            path += "&role=" + role;
        }
        if (searchText) {
            path += "&searchText=" + searchText;
        }
        history.push(path);
    };

    const newUser = async event => {
        event.preventDefault();
        await stores.appStore.setLoading(true);
        await history.push("/users/new");
        await stores.appStore.setLoading(false);
    };

    const getFilteredUsersByStatus = disabled => {
        return selectedRows.filter(user => user.disabled === disabled);
    };

    const activationForUsers = async () => {
        const usersToActivate = getFilteredUsersByStatus(true);

        if (usersToActivate.length === 0) {
            message.info("Aktifleştirilecek kullanıcı bulunamadı");
            return;
        }

        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: `${usersToActivate.length} kullanıcıyı aktifleştirmek istediğinize emin misiniz?`,
            onOk: async () => {
                setLoading(true);
                try {
                    const userIds = usersToActivate.map(user => user._id);
                    await stores.userStore.bulkUpdateUsers({
                        userIds,
                        updateData: {disabled: false}
                    });

                    message.success(
                        `${usersToActivate.length} kullanıcı başarıyla aktifleştirildi`
                    );

                    setSelectedRowKeys([]);
                    setSelectedRows([]);
                    setSelectAll(false);
                    setData(
                        await stores.userStore.getUserList({
                            limit: 10,
                            skip: (currentPage - 1) * 10,
                            activity,
                            searchText: searchText && searchText.toLocaleLowerCase("tr-TR"),
                            role
                        })
                    );
                } catch (error) {
                    console.error("Kullanıcıları aktifleştirirken hata oluştu:", error);
                    message.error("Kullanıcıları aktifleştirirken bir hata oluştu");
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const deactivationForUsers = async () => {
        const usersToDeactivate = getFilteredUsersByStatus(false);

        if (usersToDeactivate.length === 0) {
            message.info("Pasifleştirilecek kullanıcı bulunamadı");
            return;
        }

        stores.appStore.openConfirmModal({
            title: t("modal.confirmTitle", {ns: "common"}),
            content: `${usersToDeactivate.length} kullanıcıyı pasifleştirmek istediğinize emin misiniz?`,
            onOk: async () => {
                setLoading(true);
                try {
                    const userIds = usersToDeactivate.map(user => user._id);
                    await stores.userStore.bulkUpdateUsers({
                        userIds,
                        updateData: {disabled: true}
                    });

                    message.success(
                        `${usersToDeactivate.length} kullanıcı başarıyla pasifleştirildi`
                    );

                    setSelectedRowKeys([]);
                    setSelectedRows([]);
                    setSelectAll(false);
                    setData(
                        await stores.userStore.getUserList({
                            limit: 10,
                            skip: (currentPage - 1) * 10,
                            activity,
                            searchText: searchText && searchText.toLocaleLowerCase("tr-TR"),
                            role
                        })
                    );
                } catch (error) {
                    console.error("Kullanıcıları pasifleştirirken hata oluştu:", error);
                    message.error("Kullanıcıları pasifleştirirken bir hata oluştu");
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    return useObserver(() => (
        <div data-testid="usersScreen">
            <Row
                type="flex"
                justify="space-between"
                data-testid="filterCard"
                style={{width: "100%", display: "flex", alignItems: "center", marginBottom: "20px"}}
            >
                <Col lg={18} xl={18} xxl={18}>
                    <Row type="flex" gutter={[20, 20]} data-testid="filterCard">
                        <Col xs={20} sm={16} md={12} lg={8} xl={5} span={5}>
                            <SelectField
                                data-testid="userActivitySelect"
                                value={activity}
                                onChange={value => {
                                    setCurrentPage(1);
                                    setActivity(value);
                                    clearAllSelections();
                                }}
                            >
                                <SelectField.Option
                                    value="all"
                                    data-testid="userActivitySelect_option"
                                >
                                    {t("filters.all", {ns: "common"})}
                                </SelectField.Option>
                                <SelectField.Option
                                    value="enabled"
                                    data-testid="userActivitySelect_option"
                                >
                                    {t("userPage.fields.activeUsers", {ns: "userPage"})}
                                </SelectField.Option>
                                <SelectField.Option
                                    value="disabled"
                                    data-testid="userActivitySelect_option"
                                >
                                    {t("userPage.fields.inactiveUsers", {ns: "userPage"})}
                                </SelectField.Option>
                            </SelectField>
                        </Col>
                        <Col xs={20} sm={16} md={12} lg={8} xl={5} span={5}>
                            <UserTypeFilter
                                data-testid="roleSelect"
                                hideTitle={true}
                                roles={isAgency ? agencyListedRoles : undefined}
                                value={role}
                                onChange={value => {
                                    setCurrentPage(1);
                                    setRole(value);
                                    clearAllSelections();
                                }}
                            />
                        </Col>
                        <Col xs={20} sm={16} md={12} lg={8} xl={6} span={5}>
                            <SearchInput
                                placeholder={t("filters.search", {ns: "common"})}
                                value={text}
                                data-testid="searchInput"
                                onSearch={value => {
                                    setCurrentPage(1);
                                    if (value) {
                                        const reservedCharacters = /[!@#$&*(),?:\[\]]/g;
                                        if (!reservedCharacters.test(value)) {
                                            setSearchText(value);
                                            clearAllSelections();
                                        } else {
                                            message.error("Arama metni özel karakter içeremez");
                                        }
                                    }
                                }}
                                onChange={event => {
                                    setText(event.target.value);
                                }}
                                enterButton={
                                    <Button
                                        data-testid="searchInput_searchButton"
                                        icon={<SearchOutlined />}
                                    />
                                }
                                suffix={
                                    text ? (
                                        <CloseIcon
                                            data-testid="searchInput_deleteButton"
                                            onClick={() => {
                                                setCurrentPage(1);
                                                setSearchText("");
                                                setText("");
                                                clearAllSelections();
                                            }}
                                        />
                                    ) : (
                                        <span />
                                    )
                                }
                            />
                        </Col>
                    </Row>
                </Col>
                <Col lg={6} xl={6} xxl={6} style={{display: "flex", justifyContent: "flex-end"}}>
                    {(stores.meStore.checkRole("userPermission") ||
                        (isAgency && stores.meStore.checkPermission("users.edit"))) && (
                        <Row gutter={[8, 8]} style={{display: "flex", justifyContent: "flex-end"}}>
                            <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                                <RightButton
                                    href="/users/new"
                                    data-testid="newUserButton"
                                    onClick={event => newUser(event)}
                                    icon={<UserAddOutlined />}
                                />
                            </Col>
                        </Row>
                    )}
                </Col>
            </Row>
            {canBulkUpdate && (
                <Row gutter={[16, 16]} style={{marginTop: 16}}>
                    <Col xs={24} sm={24} md={8} lg={8} xl={8}>
                        <SelectButtonContainer>
                            <Col xs={24} sm={24} md={24} lg={24} xl={24}>
                                {selectedRowKeys.length > 0 ? (
                                    <CustomButton onClick={clearAllSelections}>
                                        Seçimleri Temizle
                                    </CustomButton>
                                ) : (
                                    <CustomButton onClick={selectAllData}>Tümünü Seç</CustomButton>
                                )}
                            </Col>
                        </SelectButtonContainer>
                    </Col>
                    <Col xs={24} sm={24} md={16} lg={16} xl={16}>
                        <ButtonGroup>
                            <CustomButton
                                type="primary"
                                onClick={activationForUsers}
                                disabled={getFilteredUsersByStatus(true).length === 0}
                                icon={<CheckOutlined />}
                            >
                                {getFilteredUsersByStatus(true).length} Kullanıcı Aktif Et
                            </CustomButton>
                            <CustomButton
                                type="primary"
                                danger
                                onClick={deactivationForUsers}
                                disabled={getFilteredUsersByStatus(false).length === 0}
                                icon={<CloseOutlined />}
                            >
                                {getFilteredUsersByStatus(false).length} Kullanıcı Pasif Et
                            </CustomButton>
                        </ButtonGroup>
                    </Col>
                </Row>
            )}
            <ResponsiveDataTable
                loading={loading}
                dataSource={data.rows}
                contentAlign="right"
                rowKey="_id"
                data-testid="usersTable"
                rowSelection={
                    !canBulkUpdate
                        ? undefined
                        : {
                              selectedRowKeys,
                              onChange: (selectedRowKeys, selectedRows) => {
                                  handleRowSelection(selectedRowKeys, selectedRows);
                              }
                          }
                }
                pagination={{
                    total: data.dataCount,
                    current: currentPage,
                    onChange: (page, pageSize) => {
                        setCurrentPage(page);
                    },
                    hideOnSinglePage: true,
                    showSizeChanger: false
                }}
                locale={{
                    emptyText: <EmptyMessage />
                }}
                scroll={{x: "max-content"}}
                display={data?.rows?.length > 0 ? "grid" : "flex"}
            >
                <Column
                    title={t("actions.detail", {ns: "common"})}
                    dataIndex="_id"
                    render={text => (
                        <DetailButton
                            data-testid="detailButton"
                            href={"/users/" + text}
                            onClick={event => {
                                event.preventDefault();
                                window.open("/users/" + text, "_blank");
                            }}
                            icon={<FileSearchOutlined />}
                        />
                    )}
                    onCell={() => ({"data-label": t("actions.detail", {ns: "common"})})}
                />
                <Column
                    dataIndex="disabled"
                    render={text => (
                        <Badge
                            data-testid="userActivityBadge"
                            color={activityColors[!text]}
                        ></Badge>
                    )}
                    onCell={() => ({"data-label": t("fields.status", {ns: "common"})})}
                />
                <Column
                    title={t("fields.fullname", {ns: "common"})}
                    dataIndex="fullName"
                    render={text => <span data-testid="fullName">{text}</span>}
                    onCell={() => ({"data-label": t("fields.fullname", {ns: "common"})})}
                />
                <Column
                    title={t("fields.username", {ns: "common"})}
                    dataIndex="username"
                    render={text => <span data-testid="username">{text}</span>}
                    onCell={() => ({"data-label": t("fields.username", {ns: "common"})})}
                />
                {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                    <Column
                        title={t("userPage.fields.companyName", {ns: "userPage"})}
                        dataIndex="company"
                        render={text => <span data-testid="company">{text?.companyName}</span>}
                        onCell={() => ({
                            "data-label": t("userPage.fields.companyName", {ns: "userPage"})
                        })}
                    />
                )}
                {stores.meStore.checkRole("notVisibleForCompanyUser") && (
                    <Column
                        title={t("fields.role", {ns: "common"})}
                        dataIndex="role"
                        render={text => (
                            <span data-testid="role">
                                {isConstantExist(userTypes, text)
                                    ? t(getConstant(userTypes, text).translation_key, {
                                          ns: "userTypes"
                                      })
                                    : "-"}
                            </span>
                        )}
                        onCell={() => ({"data-label": t("fields.role", {ns: "common"})})}
                    />
                )}
                <Column
                    title={t("fields.email", {ns: "common"})}
                    dataIndex="email"
                    onCell={() => ({"data-label": t("fields.email", {ns: "common"})})}
                />
            </ResponsiveDataTable>
            {selectedRowKeys.length > 0 && (
                <>
                    <Badge
                        count={selectedRowKeys.length}
                        style={{backgroundColor: "#BCEE68", color: "#000", marginRight: "10px"}}
                    />
                    <span>kullanıcı seçildi</span>
                </>
            )}
        </div>
    ));
};
